import React, { useEffect, useState } from 'react';
import api from '../../../services/api';

interface CoachingStats {
  totalNotes: number;
  acknowledgedCount: number;
  pendingCount: number;
  acknowledgementRate: number;
  topSignals: Array<{ signalId: number; count: number }>;
  manualNoteCount: number;
  autoNoteCount: number;
}

const SIGNAL_NAMES: Record<number, string> = {
  1: 'Zone Coverage',
  2: 'Face On Camera',
  3: 'Lighting',
  4: 'Stability',
  5: 'Duration',
  6: 'Speech Pace',
  7: 'Energy',
  8: 'Buyer Name',
  9: 'Feature Callout',
  10: 'Call to Action',
};

const CoachingStatsTab: React.FC = () => {
  const [stats, setStats] = useState<CoachingStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/reports/coaching-stats');
        setStats(res.data.data as CoachingStats);
      } catch {
        setStats(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="py-20 flex flex-col items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
        <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">
          school
        </span>
        <p className="text-slate-400 dark:text-slate-600 text-sm font-medium mt-2">
          Coaching stats are unavailable.
        </p>
      </div>
    );
  }

  const ackPct = Math.round(stats.acknowledgementRate * 100);
  const maxSignalCount = Math.max(1, ...stats.topSignals.map((s) => s.count));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Total Notes', value: stats.totalNotes, suffix: 'notes', icon: 'school', color: 'text-primary' },
          { label: 'Acknowledged', value: stats.acknowledgedCount, suffix: 'acked', icon: 'check_circle', color: 'text-emerald-500' },
          { label: 'Pending', value: stats.pendingCount, suffix: 'pending', icon: 'hourglass_top', color: 'text-amber-500' },
          { label: 'Ack Rate', value: `${ackPct}%`, suffix: 'ack rate', icon: 'auto_awesome', color: 'text-indigo-500' },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center ${s.color} shadow-sm border border-slate-100 dark:border-slate-700`}
              >
                <span className="material-symbols-outlined text-[24px]">{s.icon}</span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">
                {s.label}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                {s.value}
              </span>
              <span className="text-xs font-medium text-slate-400 dark:text-slate-600">{s.suffix}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-6">
            Most Flagged Signals
          </h2>
          {stats.topSignals.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-600">No coaching activity yet.</p>
          ) : (
            <div className="space-y-4">
              {stats.topSignals.map((s) => {
                const pct = (s.count / maxSignalCount) * 100;
                return (
                  <div key={s.signalId} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-900 dark:text-white">
                        {SIGNAL_NAMES[s.signalId] ?? `Signal ${s.signalId}`}
                      </span>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        {s.count} notes
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-1000"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-6">
            Note Sources
          </h2>
          <div className="space-y-4">
            {[
              { label: 'Auto-generated', value: stats.autoNoteCount, color: 'bg-indigo-500' },
              { label: 'Manual (Manager)', value: stats.manualNoteCount, color: 'bg-amber-500' },
            ].map((row) => {
              const total = stats.autoNoteCount + stats.manualNoteCount;
              const pct = total > 0 ? (row.value / total) * 100 : 0;
              return (
                <div key={row.label} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 dark:text-white">{row.label}</span>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      {row.value} ({pct.toFixed(0)}%)
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${row.color} rounded-full transition-all duration-1000`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachingStatsTab;
